import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  usePlmCurtailmentPlanDetailQuery,
  useCreatePlmCurtailmentPlanMutation,
  useUpdatePlmCurtailmentPlanMutation,
  useOrganizationsQuery,
  EnumPlmCurtailmentPlanApplicableSeasons,
  Organization,
  useRemovePlmCurtailmentActionMutation,
  usePlmCurtailmentActionDetailQuery,
  usePlmCurtailmentPlanEventsByIdsSubscription,
} from '../../../../types/generated-types';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Notifier } from '../../../system/services/notificationManager';
import BorderedSection from '../../shared/borderedSection';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useInjectableComponents } from '../../../system/services/injectableComponentsManager';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { CurtailmentActionDetailDialog } from './plmCurtailmentAction-detail-dialog';
import { useIsSmallScreen } from '../../../system/SystemMediaQueriesProvider';
import { useAuthorizer } from '../../../auth/AuthorizationContext';

interface FormValues {
  name: string;
  description: string;
  estimatedReductionKW: number;
  durationMinutes: number;
  applicableSeasons: EnumPlmCurtailmentPlanApplicableSeasons[];
  organizationId: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  estimatedReductionKW: Yup.number()
    .required('Estimated reduction is required')
    .min(0, 'Estimated reduction must be positive'),
  durationMinutes: Yup.number()
    .required('Duration is required')
    .min(1, 'Duration must be at least 1 minute'),
  applicableSeasons: Yup.array()
    .of(Yup.string())
    .required('At least one season must be selected'),
  organizationId: Yup.string().required('Organization is required'),
});

export const PlmCurtailmentPlanDetailForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuthorizer();
  const isEdit = !!id;
  const [tabValue, setTabValue] = React.useState(0);
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editActionId, setEditActionId] = React.useState<string | null>(null);
  const [deleteActionId, setDeleteActionId] = React.useState<string | null>(
    null,
  );
  const { setContextMenuItems, setTitle, setTitlePath } =
    useInjectableComponents();
  const isSmall = useIsSmallScreen();

  React.useEffect(() => {
    setTitle(
      isEdit
        ? isSmall
          ? 'Edit PLM Curtailment Plan'
          : 'Edit Peak Load Management Curtailment Plan'
        : isSmall
          ? 'Add PLM Curtailment Plan'
          : 'Create Peak Load Management Curtailment Plan',
    );
    setTitlePath('/plmCurtailmentPlan');

    return () => {
      setTitle(undefined);
      setTitlePath(undefined);
    };
  }, [setTitle, setTitlePath, isEdit, isSmall]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { data: planData, refetch } = usePlmCurtailmentPlanDetailQuery({
    variables: { _id: id! },
    skip: !isEdit,
  });
  const { data: orgData } = useOrganizationsQuery();
  const [createPlan] = useCreatePlmCurtailmentPlanMutation();
  const [updatePlan] = useUpdatePlmCurtailmentPlanMutation();
  const [removeAction] = useRemovePlmCurtailmentActionMutation();
  const { data: editActionData } = usePlmCurtailmentActionDetailQuery({
    variables: { _id: editActionId! },
    skip: !editDialogOpen || !editActionId,
  });

  const initialValues: FormValues = {
    name: planData?.plmCurtailmentPlanById?.name ?? '',
    description: planData?.plmCurtailmentPlanById?.description ?? '',
    estimatedReductionKW:
      planData?.plmCurtailmentPlanById?.estimatedReductionKW ?? 0,
    durationMinutes: planData?.plmCurtailmentPlanById?.durationMinutes ?? 0,
    applicableSeasons:
      planData?.plmCurtailmentPlanById?.applicableSeasons?.filter(
        (season): season is EnumPlmCurtailmentPlanApplicableSeasons =>
          season !== null,
      ) ?? [],
    organizationId: planData?.plmCurtailmentPlanById?.organizationId ?? '',
  };

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          await updatePlan({
            variables: {
              _id: id!,
              plan: values,
            },
          });
          Notifier.success('Curtailment plan updated successfully');
        } else {
          await createPlan({
            variables: {
              plan: values,
            },
          });
          Notifier.success('Curtailment plan created successfully');
        }
        navigate('/plmCurtailmentPlan');
      } catch (error) {
        Notifier.error(
          `Failed to ${isEdit ? 'update' : 'create'} curtailment plan`,
        );
      }
    },
  });

  // Curtailment Actions columns
  const curtailmentActionsColumns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'deviceType',
      header: 'Device Type',
    },
    {
      accessorKey: 'estimatedReductionKW',
      header: 'Estimated Reduction (kW)',
    },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => {
              setEditActionId(row.original._id);
              setEditDialogOpen(true);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setDeleteActionId(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      enableColumnActions: false,
      enableSorting: false,
    },
  ];

  // Add Curtailment Action context menu action
  React.useEffect(() => {
    if (tabValue === 1 && isEdit) {
      setContextMenuItems([
        {
          items: [
            {
              id: 'add-curtailment-action',
              icon: <AddCircleOutlineIcon />,
              label: 'Add Curtailment Action',
              action: () => setAddDialogOpen(true),
              permit: { action: 'create', subject: 'PlmCurtailmentPlan' },
            },
          ],
        },
      ]);
    } else {
      setContextMenuItems(undefined);
    }
  }, [tabValue, isEdit, setContextMenuItems]);

  // Handler for delete
  const handleDelete = async () => {
    if (!deleteActionId) return;
    try {
      await removeAction({
        variables: { _id: deleteActionId },
        update: (cache) => {
          // Remove the deleted action from the cache
          cache.evict({ id: `PlmCurtailmentAction:${deleteActionId}` });
          cache.gc();
        },
      });
      Notifier.success('Curtailment action deleted');
      setDeleteActionId(null);
      // Refetch the plan detail to update the list
      await refetch();
    } catch (error) {
      Notifier.error('Failed to delete curtailment action');
    }
  };

  usePlmCurtailmentPlanEventsByIdsSubscription({
    variables: { _ids: [id!] },
    onData: async () => {
      await refetch();
    },
  });

  return (
    <Box>
      <Box sx={{ marginTop: '15px', paddingBottom: '15px' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="secondary"
          textColor="secondary"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root:hover': {
              borderBottom: '2px solid lightbredlue',
            },
          }}
        >
          <Tab label="General Information" />
          <Tab label="Curtailment Actions" />
        </Tabs>
      </Box>
      {tabValue === 0 && (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 2 }}>
          <BorderedSection title="General Information">
            <TextField
              fullWidth
              margin="normal"
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              margin="normal"
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              error={
                formik.touched.description && Boolean(formik.errors.description)
              }
              helperText={
                formik.touched.description && formik.errors.description
              }
            />
            <TextField
              fullWidth
              margin="normal"
              name="estimatedReductionKW"
              label="Estimated Reduction (kW)"
              type="number"
              value={formik.values.estimatedReductionKW}
              onChange={formik.handleChange}
              error={
                formik.touched.estimatedReductionKW &&
                Boolean(formik.errors.estimatedReductionKW)
              }
              helperText={
                formik.touched.estimatedReductionKW &&
                formik.errors.estimatedReductionKW
              }
            />
            <TextField
              fullWidth
              margin="normal"
              name="durationMinutes"
              label="Duration (minutes)"
              type="number"
              value={formik.values.durationMinutes}
              onChange={formik.handleChange}
              error={
                formik.touched.durationMinutes &&
                Boolean(formik.errors.durationMinutes)
              }
              helperText={
                formik.touched.durationMinutes && formik.errors.durationMinutes
              }
            />
            <FormControl
              fullWidth
              margin="normal"
              error={
                formik.touched.applicableSeasons &&
                Boolean(formik.errors.applicableSeasons)
              }
            >
              <InputLabel>Applicable Seasons</InputLabel>
              <Select
                multiple
                name="applicableSeasons"
                value={formik.values.applicableSeasons}
                onChange={formik.handleChange}
                label="Applicable Seasons"
              >
                <MenuItem value="SUMMER">Summer</MenuItem>
                <MenuItem value="WINTER">Winter</MenuItem>
                <MenuItem value="YEAR_ROUND">Year Round</MenuItem>
              </Select>
              {formik.touched.applicableSeasons &&
                formik.errors.applicableSeasons && (
                  <FormHelperText>
                    {String(formik.errors.applicableSeasons)}
                  </FormHelperText>
                )}
            </FormControl>
            <FormControl
              fullWidth
              margin="normal"
              error={
                formik.touched.organizationId &&
                Boolean(formik.errors.organizationId)
              }
            >
              <InputLabel>Organization</InputLabel>
              <Select
                name="organizationId"
                value={formik.values.organizationId}
                onChange={formik.handleChange}
                label="Organization"
              >
                {orgData?.organizations?.map(
                  (org: Pick<Organization, '_id' | 'name'>) => (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  ),
                )}
              </Select>
              {formik.touched.organizationId &&
                formik.errors.organizationId && (
                  <FormHelperText>
                    {String(formik.errors.organizationId)}
                  </FormHelperText>
                )}
            </FormControl>
          </BorderedSection>
          <Box
            sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/plmCurtailmentPlan')}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              color="secondary"
              startIcon={<SaveIcon />}
            >
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      )}
      {tabValue === 1 && (
        <BorderedSection title="Curtailment Actions">
          {!isEdit ? (
            <Typography variant="body1" color="text.secondary">
              Save the plan before adding curtailment actions.
            </Typography>
          ) : (
            <>
              <MaterialReactTable
                columns={curtailmentActionsColumns}
                data={
                  planData?.plmCurtailmentPlanById?.curtailmentActions?.filter(
                    (action): action is NonNullable<typeof action> =>
                      action !== null,
                  ) ?? []
                }
                enableColumnFilters={false}
                enableColumnActions={false}
                enableColumnResizing={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableRowSelection={false}
                enableTopToolbar={true}
                muiTableBodyRowProps={{ hover: false }}
                muiTableHeadCellProps={{
                  sx: {
                    fontWeight: 'bold',
                    fontSize: '14px',
                    p: '0.5rem',
                  },
                }}
                muiTableBodyCellProps={{
                  sx: {
                    fontSize: '14px',
                    p: '0.5rem',
                  },
                }}
              />
              <CurtailmentActionDetailDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                planId={id!}
                onSuccess={() => {
                  // Refetch plan detail query or update cache here
                }}
              />
              <CurtailmentActionDetailDialog
                open={editDialogOpen}
                onClose={() => {
                  setEditDialogOpen(false);
                  setEditActionId(null);
                }}
                action={
                  editActionData?.plmCurtailmentActionById
                    ? {
                        ...editActionData.plmCurtailmentActionById,
                        __typename: 'PlmCurtailmentAction',
                      }
                    : null
                }
                onSuccess={() => {
                  setEditDialogOpen(false);
                  setEditActionId(null);
                  // Refetch plan detail query or update cache here
                }}
              />
              {/* Delete confirmation dialog */}
              {deleteActionId && (
                <Dialog open onClose={() => setDeleteActionId(null)}>
                  <DialogTitle>Delete Curtailment Action</DialogTitle>
                  <DialogContent>
                    Are you sure you want to delete this curtailment action?
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setDeleteActionId(null)}>
                      Cancel
                    </Button>
                    <Button color="error" onClick={handleDelete}>
                      Delete
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </>
          )}
        </BorderedSection>
      )}
    </Box>
  );
};
